import { interpolateNumber } from 'd3-interpolate';
import styled from 'styled-components';

import { colorize, formatter } from '../helpers';

const LegendContainer = styled.div`
	margin-bottom: 1rem;
`;

const LegendTextContainer = styled.div`
	align-items: end;
	display: flex;
	justify-content: space-between;
`;

const StyledLegendText = styled.p`
	font-size: 14px;
	line-height: 1.2;
	margin: 0;
`;

const LegendBar = styled.div`
	display: flex;
	height: 25px;
	position: relative;
`;

const LegendStop = styled.div`
	flex: 1;
	height: 100%;
`;

interface LegendTextProps {
	align: 'left' | 'right' | 'center';
	flexgrow?: number;
	value: number;
	year: number;
}

const LegendText = ({ align, flexgrow, value, year }: LegendTextProps) => {
	return value === 0 ? (
		<StyledLegendText style={{ textAlign: align, flexGrow: flexgrow }}>
			No Change
			<br />
			In Taxes
			<br />
			<span style={{ color: 'hsl(210, 58%, 33%)' }}>▾</span>
		</StyledLegendText>
	) : (
		<StyledLegendText style={{ textAlign: align, flexGrow: flexgrow }}>
			{formatter(Math.abs(value), '$')} or More
			<br />
			Tax {value < 0 ? 'Cut' : 'Increase'} in {year}
			<br />
			<span style={{ color: 'hsl(210, 58%, 33%)' }}>▾</span>
		</StyledLegendText>
	);
};

interface LegendProps {
	domain: number[];
	steps: number;
	year: number;
}

const Legend = ({ domain, steps, year }: LegendProps) => {
	// domain might be [0, positive] or [negative, 0, positive]
	const hasZero = domain[0] < 0 && domain.at(-1)! > 0;

	return (
		<LegendContainer>
			<LegendTextContainer>
				<LegendText
					align="left"
					value={domain[0]}
					flexgrow={calculateFlexGrow(domain[0], domain)}
					year={year}
				/>
				{hasZero && <LegendText align="center" value={0} year={year} />}
				<LegendText
					align="right"
					value={domain[hasZero ? 2 : 1]}
					flexgrow={calculateFlexGrow(domain[hasZero ? 2 : 1], domain)}
					year={year}
				/>
			</LegendTextContainer>
			<LegendBar>
				{[...Array(steps).keys()].map(k => (
					<LegendStop
						key={`legend-${k}`}
						style={{
							backgroundColor: colorize(
								interpolateNumber(domain[0], domain.at(-1)!)(k / steps),
								domain,
							).hex(),
						}}
					/>
				))}
			</LegendBar>
		</LegendContainer>
	);
};

function calculateFlexGrow(value: number, domain: number[]) {
	if (domain.length === 2) return 1;
	const negativeSection = Math.abs(domain[0]);
	const positiveSection = Math.abs(domain.at(-1)!);
	return value < 0
		? negativeSection / (negativeSection + positiveSection)
		: positiveSection / (negativeSection + positiveSection);
}

export default Legend;
